/**
 * Pixi application, layer wiring, responsive letterbox and the post chain.
 *
 * `createApp()` is the only place in the project that touches the renderer, the
 * canvas or the DOM host. Everything else works in design-space units through
 * the layer handles it returns.
 *
 * ## Renderer choice
 *
 * `preference` defaults to WebGL and pins WebGL 2. That is deliberate and worth
 * understanding before changing it: this whole branch exists to push real GLSL
 * shader work (per-pixel card lighting against `LIGHT`, generated normal maps,
 * foil, and the post chain below), and every custom `Filter`/`Shader` here is
 * GLSL-only. Pixi silently *skips* a filter that has no `gpuProgram` when the
 * renderer is WebGPU, so selecting WebGPU today would quietly delete the
 * lighting model rather than fall back to it.
 *
 * The option is exposed rather than hard-coded: pass `preference: 'webgpu'`
 * (Pixi will still fall back to WebGL2 when WebGPU is unavailable) once every
 * custom shader in `src/pixi/filters/` and `src/pixi/card.ts` ships a WGSL
 * `gpuProgram` alongside its `glProgram`.
 *
 * ## Frame loop
 *
 * With `autoStart` (the default) the app drives itself: per-frame callbacks
 * registered through `onUpdate()` run first, then the post chain advances its
 * uniforms, then Pixi renders. Pass `autoStart: false` and call `update(dt)`
 * yourself if the host wants to own the loop — but do one or the other, not
 * both.
 *
 * ## Wiring the other modules
 *
 * ```ts
 * const pixi = await createApp(document.getElementById('stage')!);
 *
 * pixi.layers.table.addChild(createTable());
 *
 * // VFX owns the impulse and the decay for both post signals; this module
 * // owns the transform and the uniform they land on.
 * const vfx = new VFX({
 *   onBloom: (boost) => pixi.postfx.setBloomBoost(boost),
 *   onShake: (x, y, rotation) => pixi.stage.setShake(x, y, rotation),
 * });
 * pixi.layers.vfx.addChild(vfx.view);
 * pixi.onUpdate((dt) => vfx.update(dt));
 *
 * // Input hit-tests in design units, so it never sees the letterbox.
 * pixi.canvas.addEventListener('pointerdown', (e) => {
 *   const { x, y } = pixi.toDesignSpace(e.clientX, e.clientY);
 * });
 * ```
 */

import { Application, Ticker, UPDATE_PRIORITY } from 'pixi.js';
import type { RendererPreference } from 'pixi.js';

import { palette } from '@/core/tokens';
import { PostFX, createPostFX } from '@/pixi/postfx';
import type { PostFXOptions } from '@/pixi/postfx';
import { Stage, createStage } from '@/pixi/stage';
import { configureLayout, orientation, setHudInsets } from '@/pixi/layout';
import { MAX_RESOLUTION, budgetedResolution } from '@/pixi/quality';
import type { DesignPoint, Layers } from '@/pixi/stage';
import { destroyApp } from '@/pixi/pixi-destroy';

export type { DesignPoint, LayerName, Layers } from '@/pixi/stage';
export type { PostFXOptions } from '@/pixi/postfx';
export { Stage } from '@/pixi/stage';
export { PostFX } from '@/pixi/postfx';

/**
 * Hard ceiling on render resolution. Defined in `@/pixi/quality`, which owns the
 * pixel budget this is the floor of, and re-exported here for its callers.
 */
export { MAX_RESOLUTION };


/** Per-frame callback. `dt` is seconds, `elapsed` is seconds since start. */
export type UpdateCallback = (dt: number, elapsed: number) => void;

export interface CreateAppOptions {
  /**
   * Renderer preference. Defaults to `'webgl'` — see the note at the top of
   * this file before switching it to `'webgpu'`.
   */
  preference?: RendererPreference;
  /** Multisampling on the main target and on the filter chain. Default true. */
  antialias?: boolean;
  /** Ceiling for the device pixel ratio. Default `MAX_RESOLUTION` (2). */
  maxResolution?: number;
  /** Drive the frame loop internally. Default true. */
  autoStart?: boolean;
  /** Pause rendering while the document is hidden. Default true. */
  pauseWhenHidden?: boolean;
  /** Post chain tuning. */
  postfx?: PostFXOptions;
  /** Skip the post chain entirely (it is still constructed, just disabled). */
  postProcessing?: boolean;
}

export interface PixiApp {
  readonly app: Application;
  readonly canvas: HTMLCanvasElement;
  readonly stage: Stage;
  readonly layers: Layers;
  readonly postfx: PostFX;

  /** Re-measure the host and re-fit the design rect. Called automatically. */
  resize(): void;

  /**
   * Extra room to leave at the bottom of the felt, in CSS pixels.
   *
   * For the first-run walkthrough, whose narration sits over the table.
   *
   * Taken off whichever end the streak badge is not using: the top in portrait,
   * where the badge flies to the foot of the screen, and the bottom in
   * landscape, where it sits at 27% of the height. Moving the words instead of
   * the game keeps every effect where the player will actually meet it.
   *
   * Applied as a one-off at each end of the walkthrough rather than tracked
   * continuously: a felt that re-fits mid-play takes the play sequence with it.
   */
  setGuideInset(pixels: number): void;

  /**
   * Advance one frame by hand and render. Only for `autoStart: false`.
   *
   * @param dt - elapsed seconds since the previous update.
   */
  update(dt: number): void;

  /** Alias of `update`, kept for the name used in CONTRACTS.md. */
  render(dt: number): void;

  /** Register a per-frame callback. Returns an unsubscribe function. */
  onUpdate(callback: UpdateCallback): () => void;

  /**
   * Register a callback for a change of screen orientation.
   *
   * Fires after `@/pixi/layout` has switched design rects and the stage has
   * adopted the new one, and before the next frame is drawn. Everything that
   * baked the old rect — the table's shader, the pile badges, the positions of
   * the cards themselves — has to be rebuilt from here.
   */
  onRelayout(callback: () => void): () => void;

  /** Convert a DOM client position into design-space units. */
  toDesignSpace(clientX: number, clientY: number): DesignPoint;

  start(): void;
  stop(): void;

  /**
   * Freeze the table under a shell screen.
   *
   * The home, the history, a challenge page: the felt is behind a dimmed,
   * blurred scrim and nothing on it needs to move, yet the ticker kept
   * rendering it — full resolution, bloom and grain and all — every frame,
   * under a scroller. On a phone that is the scroll "pulling": the GPU is
   * spending the scroll's budget on a picture nobody can see. Idle draws one
   * last frame and stops; a resize still redraws it, and `setIdle(false)`
   * before a deal brings it back. Blockari pauses its backdrop the same way.
   */
  setIdle(idle: boolean): void;

  /**
   * Hear about the picture going wrong: the frame loop stopping under a table
   * that should be drawing, or the GPU context being taken away and not given
   * back. See `RendererHealth`.
   */
  onHealth(callback: (health: RendererHealth) => void): () => void;

  /**
   * What the GPU is and how the last stretch went, as short lines for a
   * screenshot from a player's phone (`?diag=1`).
   */
  diagnostics(): string[];

  destroy(): void;
}

/**
 * What the watchdog saw.
 *
 * - `stalled`: no frame for `STALL_MS` while one was due; the loop was kicked.
 * - `recovered`: frames are coming again after a stall.
 * - `lost`: the context was lost and has stayed lost for `LOST_GIVE_UP_MS` with
 *   the tab visible. Nothing in-page can draw; the player has to reload.
 * - `restored`: the context came back after a `lost`.
 * - `blank`: frames are being drawn and the picture is pure black. `detail` is
 *   `post-chain-off` (the screen filters were detached to try to get a picture)
 *   or `still-black` (that did not help; nothing more can be done in-page).
 */
export interface RendererHealth {
  kind: 'stalled' | 'recovered' | 'lost' | 'restored' | 'blank';
  /** How many times the loop has been kicked since the last good frame. */
  attempts: number;
  detail?: 'post-chain-off' | 'still-black';
  /** Milliseconds since the last frame (stalled) or since the loss (lost). */
  ms: number;
}

/** A frame is overdue after this long; a phone under load can go 500 ms between them. */
export const STALL_MS = 1500;
/** Looked at this often. */
const WATCH_MS = 500;
/** A context that stays lost this long while visible is not coming back on its own. */
export const LOST_GIVE_UP_MS = 4000;

const DEFAULTS = {
  preference: 'webgl' as RendererPreference,
  antialias: true,
  maxResolution: MAX_RESOLUTION,
  autoStart: true,
  pauseWhenHidden: true,
  postProcessing: true,
};

/**
 * Fraction of a landscape phone's height reserved for browser chrome.
 *
 * 12% is about 47px on a 390px-tall landscape iPhone, which clears Safari's
 * compact toolbar with room to spare. Raise it if a browser is found that takes
 * more; it is deliberately one number in one place.
 */
const CHROME_BAND = 0.12;

class PixiAppImpl implements PixiApp {
  readonly app: Application;
  readonly stage: Stage;
  readonly layers: Layers;
  readonly postfx: PostFX;

  readonly #host: HTMLElement;
  #guideInset = 0;
  readonly #maxResolution: number;
  readonly #pauseWhenHidden: boolean;

  readonly #callbacks = new Set<UpdateCallback>();
  readonly #relayoutCallbacks = new Set<() => void>();
  /** True once this has taken the viewport over because CSS gave the host none. */
  #ownsHost = false;
  #hudObserver: ResizeObserver | null = null;

  #elapsed = 0;
  /** What the caller asked for. */
  #running = false;
  /** Forced off by a lost context or a hidden tab. */
  #suspended = false;
  #idle = false;
  #destroyed = false;

  /** The GPU context is gone (`webglcontextlost` with no `restored` since). */
  #contextLost = false;
  #lostAt = 0;
  #lostReported = false;
  #lastFrame = performance.now();
  #stallAttempts = 0;
  #stalled = false;
  #watchdog: ReturnType<typeof setInterval> | null = null;
  readonly #healthListeners = new Set<(health: RendererHealth) => void>();
  readonly #history: string[] = [];

  /** The picture check: a thumbnail of the canvas, read after the frame is drawn. */
  #probeCanvas: HTMLCanvasElement | null = null;
  #probeAt = 0;
  #blankRun = 0;
  #blankStep = 0;

  #resizeObserver: ResizeObserver | null = null;
  #dprQuery: MediaQueryList | null = null;
  #resizeFrame = 0;

  constructor(
    app: Application,
    host: HTMLElement,
    stage: Stage,
    postfx: PostFX,
    options: Required<Omit<CreateAppOptions, 'postfx'>>,
  ) {
    this.app = app;
    this.#host = host;
    this.stage = stage;
    this.layers = stage.layers;
    this.postfx = postfx;
    this.#maxResolution = options.maxResolution;
    this.#pauseWhenHidden = options.pauseWhenHidden;

    this.app.stage.addChild(stage.view);
    this.stage.canvas = this.canvas;

    this.stage.root.filters = postfx.filters;
    this.stage.cardsGroup.filters = postfx.cardFilters;
    if (!options.postProcessing) postfx.enabled = false;

    this.#styleCanvas();
    this.resize();

    // Uniforms and gameplay callbacks run at HIGH; the TickerPlugin's render
    // sits at LOW, so a frame is always advance-then-draw.
    this.app.ticker.add(this.#tick, this, UPDATE_PRIORITY.HIGH);
    // After the render (LOW): the drawing buffer is only readable until the
    // browser composites, which is the end of this task.
    this.app.ticker.add(this.#probe, this, UPDATE_PRIORITY.UTILITY);

    this.#listen();
    this.#watchHud();

    this.#watchdog = setInterval(this.#watch, WATCH_MS);

    if (options.autoStart) this.start();
  }

  get canvas(): HTMLCanvasElement {
    return this.app.canvas;
  }

  setGuideInset(pixels: number): void {
    const next = Math.max(0, Math.round(pixels));
    if (next === this.#guideInset) return;
    this.#guideInset = next;
    this.resize();
  }

  resize(): void {
    if (this.#destroyed) return;

    const { width, height } = this.#measure();
    const resolution = this.#resolution(width, height);

    // Before the fit, because the fit is against whichever design rect this
    // viewport shape calls for. A phone that has just been turned on its side
    // is a different rect, not the same rect letterboxed differently.
    //
    // The HUD insets go in first: on a small screen the rect is sized to the
    // room the HUD leaves, so measuring it afterwards would size this frame's
    // felt against the previous frame's HUD.
    // Published before the HUD is measured: it moves the HUD down, and the
    // felt is then sized to whatever room the HUD leaves. The other order
    // measures this frame's felt against last frame's HUD.
    this.#publishChromeInset();
    const hud = this.#hudInsets();
    if (hud) setHudInsets(hud);
    const reoriented = configureLayout(width, height);

    this.app.renderer.resize(width, height, resolution);
    if (reoriented) this.stage.relayout();
    this.stage.fit(width, height);
    // The stage scale goes with it so the bloom stays sized to the table
    // rather than to the window.
    this.postfx.setScreen(width, height, resolution, this.stage.scale);

    // After the stage and the post chain, so a listener rebuilding the table
    // sees the geometry it will actually be drawn with.
    if (reoriented) for (const callback of this.#relayoutCallbacks) callback();

    this.#watchPixelRatio();

    // Paint before returning.
    //
    // Resizing the renderer reallocates the framebuffer and leaves it empty.
    // This runs from a resize event rather than from the ticker, so the browser
    // composites as soon as it returns — and what it composites, without this,
    // is that empty framebuffer. Normally that is one frame and nobody sees it.
    // On a reorientation it is not: the callbacks above have just destroyed and
    // rebuilt the table, the atmosphere and every card, and the blank frame
    // stays on screen for as long as that took. Reported as "starting in a
    // small window and maximising gives a black screen for a moment", which is
    // exactly what it is.
    //
    // Rendering rather than advancing: nothing should move because the window
    // changed shape, and `#advance` at dt 0 would still step anything that
    // treats a call as a tick.
    this.app.renderer.render(this.app.stage);
  }

  update(dt: number): void {
    if (this.#destroyed) return;
    this.#advance(dt);
    this.app.renderer.render(this.app.stage);
  }

  render(dt: number): void {
    this.update(dt);
  }

  onRelayout(callback: () => void): () => void {
    this.#relayoutCallbacks.add(callback);
    return () => {
      this.#relayoutCallbacks.delete(callback);
    };
  }

  onUpdate(callback: UpdateCallback): () => void {
    this.#callbacks.add(callback);
    return () => {
      this.#callbacks.delete(callback);
    };
  }

  toDesignSpace(clientX: number, clientY: number): DesignPoint {
    return this.stage.toDesignSpace(clientX, clientY);
  }

  start(): void {
    if (this.#destroyed) return;
    this.#running = true;
    this.#syncTicker();
  }

  stop(): void {
    this.#running = false;
    this.#syncTicker();
  }

  setIdle(idle: boolean): void {
    if (this.#destroyed || idle === this.#idle) return;
    this.#idle = idle;
    this.#syncTicker();
    // Pixi's system ticker too: it runs the pointer-over updates and the
    // texture GC's scheduler, one more rAF loop under a screen with no
    // pointer on the felt and nothing being drawn.
    if (idle) Ticker.system.stop();
    else Ticker.system.start();
    if (!idle && this.#viewportStale) {
      this.#viewportStale = false;
      this.resize();
    }
    // The frame it stops on is the one that stays: drawn now rather than left
    // to whatever the ticker last managed, so a table that was mid-motion
    // does not freeze half a frame behind.
    if (idle && this.#running && !this.#suspended) this.app.renderer.render(this.app.stage);
  }

  onHealth(callback: (health: RendererHealth) => void): () => void {
    this.#healthListeners.add(callback);
    return () => {
      this.#healthListeners.delete(callback);
    };
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;

    if (this.#watchdog !== null) clearInterval(this.#watchdog);
    this.#watchdog = null;
    this.#healthListeners.clear();
    this.stop();
    this.#unlisten();
    this.#hudObserver?.disconnect();
    this.#hudObserver = null;
    this.#callbacks.clear();

    this.app.ticker.remove(this.#tick, this);
    this.app.ticker.remove(this.#probe, this);

    // Detach the chain before anything is torn down, so the renderer never
    // sees a filter whose GPU resources have already gone.
    this.stage.root.filters = [];
    this.stage.cardsGroup.filters = [];
    this.postfx.destroy();
    this.stage.destroy();

    // `children: true` clears the scene graph; textures are deliberately left
    // alone because the card atlas is owned by another module.
    destroyApp(this.app, { children: true });
  }

  // ---------------------------------------------------------------- internals

  #advance(dt: number): void {
    this.#lastFrame = performance.now();
    if (this.#stalled) {
      this.#stalled = false;
      this.#emit({ kind: 'recovered', attempts: this.#stallAttempts, ms: 0 });
      this.#stallAttempts = 0;
    }
    const step = Math.min(Math.max(dt, 0), 1 / 15);
    this.#elapsed += step;

    for (const callback of this.#callbacks) callback(step, this.#elapsed);
    this.postfx.update(step);
  }

  #tick = (): void => {
    this.#advance(this.app.ticker.deltaMS / 1000);
  };

  #emit(health: RendererHealth): void {
    this.#history.push(`${Math.round(performance.now() / 100) / 10}s ${health.kind}${health.detail ? ` ${health.detail}` : ''} ${health.ms}ms`);
    if (this.#history.length > 6) this.#history.shift();
    for (const listener of this.#healthListeners) listener(health);
  }

  diagnostics(): string[] {
    const gl = (this.app.renderer as { gl?: WebGL2RenderingContext }).gl;
    const lines: string[] = [];
    if (gl) {
      const info = gl.getExtension('WEBGL_debug_renderer_info');
      lines.push(
        info
          ? `${String(gl.getParameter(info.UNMASKED_VENDOR_WEBGL))} | ${String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL))}`
          : String(gl.getParameter(gl.RENDERER)),
      );
      lines.push(
        `${String(gl.getParameter(gl.VERSION))} | tex ${String(gl.getParameter(gl.MAX_TEXTURE_SIZE))} samples ${String(gl.getParameter(gl.MAX_SAMPLES))} units ${String(gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS))}`,
      );
      lines.push(`lost ${String(gl.isContextLost())} | filters ${this.stage.root.filters ? (this.stage.root.filters as unknown[]).length : 0} | blank step ${this.#blankStep}`);
    }
    lines.push(`canvas ${this.canvas.width}x${this.canvas.height} res ${this.app.renderer.resolution.toFixed(2)} dpr ${(globalThis.devicePixelRatio || 1).toFixed(2)}`);
    return [...lines, ...this.#history];
  }

  /**
   * Is the picture there? A black table under a live HUD has been reported from
   * a Samsung phone on open and on reload. Nothing says the ticker stopped: the
   * loop can run and draw nothing (a filter that will not compile or whose
   * target is incomplete blanks everything under it). So look at what was drawn:
   * a 16 px thumbnail, twice a second. Pure black for 1.5 s while drawing means
   * the screen filters come off; if it is still black after that, say so.
   */
  #probe = (): void => {
    if (this.#destroyed || !this.#frameDue()) return;
    const now = performance.now();
    if (now - this.#probeAt < 500) return;
    this.#probeAt = now;
    let black = false;
    try {
      const canvas = (this.#probeCanvas ??= document.createElement('canvas'));
      canvas.width = 16;
      canvas.height = 16;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx || this.canvas.width === 0) return;
      ctx.drawImage(this.canvas, 0, 0, 16, 16);
      const data = ctx.getImageData(4, 4, 8, 8).data;
      let brightest = 0;
      for (let i = 0; i < data.length; i += 4) brightest = Math.max(brightest, data[i]! + data[i + 1]! + data[i + 2]!);
      black = brightest <= 6;
    } catch {
      return;
    }
    if (!black) {
      this.#blankRun = 0;
      return;
    }
    this.#blankRun += 1;
    if (this.#blankRun < 3) return;
    this.#blankRun = 0;
    if (this.#blankStep === 0) {
      this.#blankStep = 1;
      this.stage.root.filters = [];
      this.stage.cardsGroup.filters = [];
      this.#emit({ kind: 'blank', attempts: 1, ms: 1500, detail: 'post-chain-off' });
    } else if (this.#blankStep === 1) {
      this.#blankStep = 2;
      this.#emit({ kind: 'blank', attempts: 2, ms: 3000, detail: 'still-black' });
    }
  };

  /** Whether a frame is due right now. */
  #frameDue(): boolean {
    return this.#running && !this.#suspended && !this.#idle && !document.hidden;
  }

  /**
   * The loop has been seen to stop under a table that is meant to be drawing —
   * the black table with a live HUD, reported from Android phones at the start
   * of a match. Two ways in, and the ticker is not the only one:
   *
   * - the ticker stopped or was never re-armed (a visibility flip racing a
   *   context restore, a stale idle): kick it. `stop()` then `start()` on the
   *   Pixi app re-requests the animation frame and re-bases its clock.
   * - the context was lost and the browser never restored it: nothing in-page
   *   can draw. Say so, so the player is told rather than left watching a
   *   running clock over a black felt.
   */
  #watch = (): void => {
    if (this.#destroyed) return;
    const now = performance.now();

    if (this.#contextLost) {
      if (!document.hidden && !this.#lostReported && now - this.#lostAt >= LOST_GIVE_UP_MS) {
        this.#lostReported = true;
        this.#emit({ kind: 'lost', attempts: 0, ms: Math.round(now - this.#lostAt) });
      }
      return;
    }

    if (!this.#frameDue()) {
      // Not due: the gap is not a stall, and a wake-up must not inherit it.
      this.#lastFrame = now;
      return;
    }
    if (now - this.#lastFrame < STALL_MS) return;

    this.#stalled = true;
    this.#stallAttempts += 1;
    const ms = Math.round(now - this.#lastFrame);
    this.#lastFrame = now;
    this.app.stop();
    this.app.start();
    this.#emit({ kind: 'stalled', attempts: this.#stallAttempts, ms });
  };

  #syncTicker(): void {
    if (this.#destroyed) return;
    if (this.#running && !this.#suspended && !this.#idle) this.app.start();
    else this.app.stop();
  }

  /**
   * Render resolution, capped by a pixel budget rather than a flat ratio.
   *
   * A flat cap of 2 was costing phones their sharpness for no saving. A DPR-3
   * phone at 390x844 wants 1170x2532 device pixels — 3.0 megapixels, which is
   * *fewer* than the 4.1 a 1920x1080 desktop already renders at 2x. Capping it
   * at 2 threw away a third of the linear resolution of every card face on
   * exactly the devices where the card is smallest and hardest to read.
   *
   * So the ceiling is a total pixel count, with `maxResolution` as the floor of
   * that ceiling. A phone comes out at its native 3; a 1920x1080 desktop comes
   * out at 2 exactly as before; a 4K desktop is still held at 2.
   */
  #resolution(width: number, height: number): number {
    return budgetedResolution(width, height, this.#maxResolution);
  }

  /**
   * How far the HUD reaches in from the top and bottom, in CSS pixels.
   *
   * Measured from the real elements because the CSS that sizes them clamps
   * against `vw`, `vh` and the safe-area insets, none of which this side can
   * work out. Falls back to leaving the layout's own default in place if the
   * HUD is not up yet.
   */
  #hudInsets(): { top: number; bottom: number } | null {
    const doc = globalThis.document;
    const top = doc?.querySelector('.w21-hud__top');
    const bottom = doc?.querySelector('.w21-hud__bottom');
    if (!top || !bottom) return null;

    const viewport = globalThis.innerHeight || 0;
    const topBox = top.getBoundingClientRect();
    const bottomBox = bottom.getBoundingClientRect();
    // A HUD that measures zero is one that is not laid out — keep the last
    // known claim rather than letting the felt jump while a screen is open.
    if (topBox.height <= 0 || bottomBox.height <= 0) return null;

    // The narration is not part of the HUD but takes the same kind of room, so
    // the felt is told about it the same way — at whichever end it is on.
    const atTop = orientation() === 'portrait';
    return {
      top: Math.max(0, topBox.bottom, atTop ? this.#guideInset : 0) + this.#chromeAllowance(),
      bottom: Math.max(0, viewport - bottomBox.top, atTop ? 0 : this.#guideInset),
    };
  }

  /**
   * Room kept clear at the top for browser chrome that cannot be measured.
   *
   * On a landscape phone the browser's address bar overlays the page, and on
   * iOS it does so without shrinking anything a page can read: the layout
   * viewport covers the whole screen because of `viewport-fit=cover`,
   * `env(safe-area-inset-top)` describes the notch rather than the toolbar, and
   * `visualViewport` reports full height in that state. Two attempts to detect
   * this failed for that reason, each looking as though it had worked.
   *
   * So this stops trying to detect it. A landscape phone in a browser simply
   * does not get the top of its rect, and nothing is drawn there — which is
   * true whatever the browser does with its furniture, and is the only version
   * of this that cannot be wrong about someone's chrome.
   *
   * The allowance is skipped where there is provably no chrome: an installed
   * web app has no address bar, and a mouse is not a phone. Portrait is left
   * alone because the bar sits below the content there and the rect is tall
   * enough that the overlap costs nothing.
   */
  /**
   * Hand the chrome allowance to the stylesheet.
   *
   * The felt learns about it through `setHudInsets`, but the HUD is DOM and has
   * to be pushed down itself — and the HUD is what the report was actually
   * about. A landscape phone puts the score readout about 5% from the top of
   * the frame, and a browser toolbar covers rather more than that, so the
   * number a player watches accumulate was underneath it.
   */
  #publishChromeInset(): void {
    const root = globalThis.document?.documentElement;
    if (!root) return;
    const px = this.#chromeAllowance();
    root.style.setProperty('--w21-chrome-t', `${px}px`);
    // One signal, two consumers: the felt reads the number, and the HUD reads
    // this class to know that anything which would travel *upward* has nowhere
    // to go. Set here rather than derived in two places, so they cannot
    // disagree about whether there is a toolbar.
    root.classList.toggle('w21-gain-below', px > 0);
  }

  #chromeAllowance(): number {
    if (orientation() === 'portrait') return 0;

    const mm = globalThis.matchMedia?.bind(globalThis);
    if (typeof mm !== 'function') return 0;
    // A finger, and a page rather than an installed app.
    try {
      if (!mm('(pointer: coarse)').matches) return 0;
      if (mm('(display-mode: standalone)').matches) return 0;
      if (mm('(display-mode: fullscreen)').matches) return 0;
    } catch {
      return 0;
    }
    // iOS reports this on a home-screen app and nothing else.
    if ((globalThis.navigator as { standalone?: boolean }).standalone === true) return 0;

    // Measured against the viewport rather than fixed, because a compact
    // landscape toolbar is a proportion of the screen rather than a constant.
    // Enough for Safari's landscape bar with a little over, and it costs the
    // felt a band it was not using for anything the player reads.
    return Math.round((globalThis.innerHeight || 0) * CHROME_BAND);
  }

  /**
   * Re-fit when the HUD itself changes size.
   *
   * On a small screen the felt is sized to the room the HUD leaves, so the HUD
   * settling is a layout event for the canvas. It settles at least once on
   * every load, because the display font arrives after first paint and the
   * readouts are set in it — measure only at boot and the felt is sized against
   * a HUD a good deal taller than the one the player ends up looking at.
   */
  #watchHud(): void {
    const doc = globalThis.document;
    if (!doc || typeof ResizeObserver !== 'function') return;

    const top = doc.querySelector('.w21-hud__top');
    const bottom = doc.querySelector('.w21-hud__bottom');
    if (!top || !bottom) return;

    const observer = new ResizeObserver(() => {
      if (this.#destroyed) return;
      const next = this.#hudInsets();
      // Only a change that moves the layout is worth a resize; this fires for
      // every score digit that widens the top row too.
      if (next && setHudInsets(next)) this.resize();
    });
    observer.observe(top);
    observer.observe(bottom);
    this.#hudObserver = observer;
  }

  /**
   * The box to render into, in CSS pixels.
   *
   * When the page has sized the host itself, that is the answer. When it has
   * not — the ordinary case here, since `#stage` carries no size — this takes
   * over the viewport, and then it is responsible for tracking what "the
   * viewport" actually means on a phone.
   *
   * **`position: fixed; inset: 0` is not the visible area.** It resolves
   * against the LAYOUT viewport, which on a mobile browser extends behind the
   * URL bar and the bottom toolbar. The canvas is then partly underneath the
   * browser's own furniture, and what gets hidden is whatever the game draws
   * near the top — in this game, a score popup rising out of a pile, which is
   * exactly the number the player is trying to read. Portrait has enough height
   * that nothing important lands in the overlap; landscape does not.
   *
   * So when this owns the host it pins it to `visualViewport` explicitly, every
   * time — position as well as size, because the visible area can be offset
   * from the layout viewport's origin as well as shorter than it.
   *
   * An earlier attempt at this only consulted `visualViewport` inside the
   * take-over branch, which runs exactly once: the moment it sets `inset: 0`
   * the host has a size, so every later measurement went back to reading the
   * layout viewport off the host's own rect. It fixed nothing and looked as
   * though it had.
   */
  #measure(): { width: number; height: number } {
    if (!this.#ownsHost) {
      const rect = this.#host.getBoundingClientRect();
      if (rect.width >= 1 && rect.height >= 1) {
        return { width: rect.width, height: rect.height };
      }
      // No size from CSS. Take the viewport, and keep it from here on.
      this.#ownsHost = true;
      this.#host.style.position = 'fixed';
    }

    const vv = globalThis.visualViewport;
    const width = vv?.width ?? globalThis.innerWidth;
    const height = vv?.height ?? globalThis.innerHeight;

    // Written as an explicit rect rather than `inset: 0`, so the host follows
    // the visible area rather than the layout viewport it sits inside.
    const style = this.#host.style;
    style.left = `${vv?.offsetLeft ?? 0}px`;
    style.top = `${vv?.offsetTop ?? 0}px`;
    style.width = `${width}px`;
    style.height = `${height}px`;
    style.right = 'auto';
    style.bottom = 'auto';

    return { width: Math.max(1, width), height: Math.max(1, height) };
  }

  #styleCanvas(): void {
    const host = this.#host;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.style.overflow = 'hidden';

    const style = this.canvas.style;
    style.position = 'absolute';
    style.left = '0';
    style.top = '0';
    style.display = 'block';
    // A card table is dragged on, not scrolled.
    style.touchAction = 'none';
    style.userSelect = 'none';
    style.outline = 'none';

    if (!host.contains(this.canvas)) host.appendChild(this.canvas);
  }

  /**
   * The visual viewport moved — on a phone, most often the URL bar collapsing
   * as the player scrolls a shell screen. Under one, the table is frozen and
   * behind a scrim, and every one of these was a framebuffer reallocation,
   * a refit and a redraw per frame of the bar's animation: the scroll
   * "pulling" on an iPhone. Idle, they wait; the next `setIdle(false)` (a
   * deal) measures once. A real resize (the host's box, the window) is still
   * taken at once, so a rotated phone's home is not sitting on a felt the
   * old shape.
   */
  #queueViewportResize = (): void => {
    if (this.#idle) {
      this.#viewportStale = true;
      return;
    }
    this.#queueResize();
  };

  #viewportStale = false;

  #queueResize = (): void => {
    if (this.#destroyed || this.#resizeFrame) return;
    // Coalesce: ResizeObserver, window resize and an orientation change all
    // fire together, and each one is a renderer reallocation.
    this.#resizeFrame = requestAnimationFrame(() => {
      this.#resizeFrame = 0;
      this.resize();
    });
  };

  #onVisibility = (): void => {
    if (!this.#pauseWhenHidden) return;
    // A tab coming back to the front does not bring a lost context back with it.
    this.#suspended = document.hidden || this.#contextLost;
    this.#lastFrame = performance.now();
    this.#syncTicker();
  };

  #onContextLost = (event: Event): void => {
    // Let the browser know we intend to recover, and stop drawing into a dead
    // context in the meantime.
    event.preventDefault();
    this.#contextLost = true;
    this.#lostAt = performance.now();
    this.#lostReported = false;
    this.#suspended = true;
    this.#syncTicker();
  };

  #onContextRestored = (): void => {
    // Pixi's own systems rebuild their GPU resources off the restore event; we
    // just need to re-measure and start drawing again.
    this.#suspended = document.hidden && this.#pauseWhenHidden;
    this.#contextLost = false;
    this.#lastFrame = performance.now();
    this.resize();
    this.#syncTicker();
    if (this.#lostReported) this.#emit({ kind: 'restored', attempts: 0, ms: 0 });
    this.#lostReported = false;
  };

  #listen(): void {
    this.#resizeObserver = new ResizeObserver(this.#queueResize);
    this.#resizeObserver.observe(this.#host);

    globalThis.addEventListener('resize', this.#queueResize);
    globalThis.addEventListener('orientationchange', this.#queueResize);
    // The visual viewport changes without a window resize: a URL bar
    // collapsing on scroll, or a keyboard opening, moves what is visible while
    // `innerHeight` stays put. Without these the canvas keeps the size it had
    // when the chrome was in a different state.
    globalThis.visualViewport?.addEventListener('resize', this.#queueViewportResize);
    globalThis.visualViewport?.addEventListener('scroll', this.#queueViewportResize);
    document.addEventListener('visibilitychange', this.#onVisibility);

    this.canvas.addEventListener('webglcontextlost', this.#onContextLost);
    this.canvas.addEventListener('webglcontextrestored', this.#onContextRestored);
  }

  #unlisten(): void {
    if (this.#resizeFrame) {
      cancelAnimationFrame(this.#resizeFrame);
      this.#resizeFrame = 0;
    }

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    this.#dprQuery?.removeEventListener('change', this.#queueResize);
    this.#dprQuery = null;

    globalThis.removeEventListener('resize', this.#queueResize);
    globalThis.removeEventListener('orientationchange', this.#queueResize);
    globalThis.visualViewport?.removeEventListener('resize', this.#queueViewportResize);
    globalThis.visualViewport?.removeEventListener('scroll', this.#queueViewportResize);
    document.removeEventListener('visibilitychange', this.#onVisibility);

    this.canvas.removeEventListener('webglcontextlost', this.#onContextLost);
    this.canvas.removeEventListener('webglcontextrestored', this.#onContextRestored);
  }

  /**
   * Dragging the window between a retina and a non-retina display changes
   * devicePixelRatio without firing a resize event. This is the standard way to
   * hear about it.
   */
  #watchPixelRatio(): void {
    this.#dprQuery?.removeEventListener('change', this.#queueResize);
    const dpr = globalThis.devicePixelRatio || 1;
    this.#dprQuery = globalThis.matchMedia(`(resolution: ${dpr}dppx)`);
    this.#dprQuery.addEventListener('change', this.#queueResize, { once: true });
  }
}

/**
 * Build the application, mount its canvas into `host`, and wire up the layers,
 * the letterbox and the post chain.
 */
export async function createApp(
  host: HTMLElement,
  options: CreateAppOptions = {},
): Promise<PixiApp> {
  const resolved = {
    preference: options.preference ?? DEFAULTS.preference,
    antialias: options.antialias ?? DEFAULTS.antialias,
    maxResolution: options.maxResolution ?? DEFAULTS.maxResolution,
    autoStart: options.autoStart ?? DEFAULTS.autoStart,
    pauseWhenHidden: options.pauseWhenHidden ?? DEFAULTS.pauseWhenHidden,
    postProcessing: options.postProcessing ?? DEFAULTS.postProcessing,
  };

  const rect = host.getBoundingClientRect();
  const width = Math.max(1, rect.width || globalThis.innerWidth);
  const height = Math.max(1, rect.height || globalThis.innerHeight);

  const app = new Application();

  await app.init({
    preference: resolved.preference,
    // Pixi will happily fall back to WebGL 1 and silently rewrite shaders into
    // its compatibility dialect. The multi-pass post chain and the card
    // lighting are written for WebGL 2 render targets, so ask for it explicitly
    // rather than discover the difference at runtime.
    preferWebGLVersion: 2,
    antialias: resolved.antialias,
    resolution: budgetedResolution(width, height, resolved.maxResolution),
    autoDensity: true,
    width,
    height,
    backgroundColor: palette.void,
    backgroundAlpha: 1,
    powerPreference: 'high-performance',
    // The app owns its loop; see the note at the top of the file.
    autoStart: false,
    sharedTicker: false,
    hello: false,
  });

  const stage = createStage();
  const postfx = createPostFX(options.postfx);

  return new PixiAppImpl(app, host, stage, postfx, resolved);
}
